/**
 * Root SPA component that wires up theme, auth context, router, and routes.
 * Public pages are accessible directly; protected pages are wrapped by
 * `ProtectedRoute` which guards them based on authentication state.
 */
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import theme from './theme';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Header from './components/Header';
import Login from './pages/Login';
import ApiStatusCheck from './pages/ApiStatus';
import SchoolYearSelect from './pages/SchoolYearSelect';
import ClassSelection from './pages/ClassSelection';
import GradeProcessing from './pages/GradeProcessing';
import UserManagement from './pages/UserManagement';
import ManageUserApiKeys from './pages/ManageUserApiKeys';
import StudentMappings from './pages/StudentMappings';

/**
 * Application entry component.
 * Renders the global `Header` and configures route mapping.
 */
const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <BrowserRouter>
          <div className="app">
            <Header />
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<Login />} />
              
              {/* Protected routes */}
              <Route element={<ProtectedRoute />}>
                <Route path="/api-status" element={<ApiStatusCheck />} />
                <Route path="/select-school-year" element={<SchoolYearSelect />} />
                <Route path="/class-selection" element={<ClassSelection />} />
                <Route path="/grade-processing" element={<GradeProcessing />} />
                <Route path="/user-management" element={<UserManagement />} />
                <Route path="/student-mappings" element={<StudentMappings />} />
                {/* Add route for managing specific user API keys */}
                <Route path="/users/:userId/api-keys" element={<ManageUserApiKeys />} />
              </Route>
              
              {/* Redirect to login by default */}
              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          </div>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
